import { useEffect, useState, useCallback } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatApiError, downloadInvoicePdf, downloadPdf, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Plus, FileText, Download, CreditCard, Repeat, Trash2, Truck, Bell, Mail, MessageSquare, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const money = (n) => "£" + Number(n || 0).toFixed(2);
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const NAV = [
  { key: "orders", label: "My orders", icon: Package },
  { key: "new", label: "New order", icon: Plus },
  { key: "recurring", label: "Recurring", icon: Repeat },
  { key: "invoices", label: "Invoices", icon: FileText },
  { key: "alerts", label: "Alerts", icon: Bell },
];
const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700", collected: "bg-blue-100 text-blue-700",
  processing: "bg-amber-100 text-amber-700", ready: "bg-teal-100 text-teal-700",
  out_for_delivery: "bg-indigo-100 text-indigo-700", completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

export default function ClientPortal() {
  const [tab, setTab] = useState("orders");
  const [ctx, setCtx] = useState(null);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invLoaded, setInvLoaded] = useState(false);
  const [collections, setCollections] = useState([]);

  const load = useCallback(() => {
    api.get("/client/business").then((r) => setCtx(r.data));
    api.get("/client/orders").then((r) => setOrders(r.data));
    api.get("/client/invoices").then((r) => { setInvoices(r.data); setInvLoaded(true); });
    api.get("/client/collections").then((r) => setCollections(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Order-ready push: poll and toast when an order becomes ready / out for delivery
  useEffect(() => {
    const id = setInterval(() => { api.get("/client/orders").then((r) => setOrders(r.data)).catch(() => {}); }, 30000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (!orders.length) return;
    let seen = {};
    try { seen = JSON.parse(localStorage.getItem("ld_order_status") || "{}"); } catch (e) { seen = {}; }
    const labels = { ready: "is ready for collection", out_for_delivery: "is out for delivery" };
    let changed = false;
    orders.forEach((o) => {
      if (seen[o.id] !== o.status) {
        if (labels[o.status] && seen[o.id] !== undefined) {
          toast.success(`Order #${o.ref} ${labels[o.status]}!`, { duration: 8000 });
        }
        seen[o.id] = o.status; changed = true;
      }
    });
    if (changed) localStorage.setItem("ld_order_status", JSON.stringify(seen));
  }, [orders]);

  return (
    <DashboardShell title={ctx?.business?.name || "Client portal"} nav={NAV} active={tab} onSelect={setTab}
      brand={{ logo_url: ctx?.business?.logo_url, accent_color: ctx?.business?.accent_color, name: ctx?.business?.name }}>
      {tab === "orders" && <MyOrders orders={orders} collections={collections} reload={load} ctx={ctx} />}
      {tab === "new" && <NewOrder ctx={ctx} onDone={() => { load(); setTab("orders"); }} />}
      {tab === "recurring" && <ClientRecurring ctx={ctx} />}
      {tab === "invoices" && <MyInvoices invoices={invoices} ctx={ctx} loaded={invLoaded} />}
      {tab === "alerts" && <ClientAlerts />}
    </DashboardShell>
  );
}

const STATUS_LABELS = {
  new: "Order received", collected: "Collected", processing: "In progress", ready: "Ready",
  out_for_delivery: "Out for delivery", completed: "Completed", cancelled: "Cancelled",
};

function ClientAlerts() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/client/notification-settings").then((r) => setPrefs(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const toggle = (channel, status) => setPrefs((p) => {
    const set = new Set(p[channel]);
    set.has(status) ? set.delete(status) : set.add(status);
    return { ...p, [channel]: Array.from(set) };
  });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/client/notification-settings", { email: prefs.email, sms: prefs.sms });
      setPrefs(data); toast.success("Notification preferences saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!prefs) return <p className="text-sm text-slate-400">Loading…</p>;
  const statuses = (prefs.statuses || []).filter((s) => s !== "cancelled");
  return (
    <div className="max-w-2xl" data-testid="client-alerts">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Notification preferences</h1>
      <p className="mt-1 text-sm text-slate-500">Choose exactly which order updates you want, and whether they come by email, text, or both.</p>

      <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-4 py-3">Order update</th>
              <th className="px-4 py-3 text-center"><span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> Email</span></th>
              <th className="px-4 py-3 text-center"><span className="inline-flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> SMS</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {statuses.map((s) => (
              <tr key={s} data-testid={`alert-row-${s}`}>
                <td className="px-4 py-3 text-slate-700">{STATUS_LABELS[s] || s.replace(/_/g, " ")}</td>
                <td className="px-4 py-3 text-center">
                  <Checkbox checked={prefs.email.includes(s)} onCheckedChange={() => toggle("email", s)} data-testid={`alert-email-${s}`} />
                </td>
                <td className="px-4 py-3 text-center">
                  <Checkbox checked={prefs.sms.includes(s)} onCheckedChange={() => toggle("sms", s)} disabled={!prefs.has_phone} data-testid={`alert-sms-${s}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!prefs.has_phone && <p className="mt-2 text-xs text-slate-400">Add a phone number with your laundry to enable SMS alerts.</p>}
      <Button onClick={save} disabled={saving} className="mt-4 rounded-full bg-brand hover:bg-brand-hover" data-testid="alerts-save-btn">{saving ? "Saving…" : "Save preferences"}</Button>
    </div>
  );
}

function MyOrders({ orders, collections, reload, ctx }) {
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const book = async () => {
    setBusy(true);
    try { await api.post("/client/request-collection", { preferred_date: date }); toast.success("Pickup requested — your laundry will collect it"); setDate(""); reload && reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cancel = async (id) => {
    try { await api.delete(`/client/collections/${id}`); toast.success("Pickup cancelled"); reload && reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reorder = async (o) => {
    const priceList = ctx?.items || [];
    if (!window.confirm(`Place a new order with the same items as #${o.ref}? We'll arrange a collection.`)) return;
    try {
      const items = o.items.map((i) => {
        const current = priceList.find((p) => p.id === i.item_id);
        return { item_id: i.item_id || null, name: current?.name || i.name, price: current ? current.price : i.price, qty: i.qty };
      });
      await api.post("/client/orders", { items, needs_pickup: true, needs_delivery: true, notes: `Reorder of #${o.ref}` });
      toast.success("Reorder placed — we'll arrange collection");
      reload && reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const recent = (collections || []).slice(0, 5);
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-slate-900">My orders</h1>

      <div className="mt-5 flex flex-wrap items-end gap-3 rounded-2xl border border-brand/30 bg-accent/40 p-4" data-testid="book-pickup-card">
        <div>
          <p className="font-heading font-bold text-slate-900">Need a collection?</p>
          <p className="text-sm text-slate-500">Book a pickup and we&rsquo;ll come and get your laundry.</p>
        </div>
        <div className="ml-auto flex items-end gap-2">
          <div className="space-y-1"><Label className="text-xs">Preferred date (optional)</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10" data-testid="book-pickup-date" /></div>
          <Button onClick={book} disabled={busy} className="h-10 rounded-full bg-brand hover:bg-brand-hover" data-testid="book-pickup-btn"><Truck className="mr-1 h-4 w-4" /> Book a pickup</Button>
        </div>
      </div>

      {collections && collections.length > 0 && (
        <div className="mt-5" data-testid="collection-requests">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Pickup requests</h2>
          <div className="mt-2 space-y-2">
            {recent.map((j) => (
              <div key={j.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm" data-testid={`collection-${j.id}`}>
                <span>Pickup {j.scheduled_date ? `· ${j.scheduled_date}` : "· ASAP"}</span>
                <div className="flex items-center gap-2">
                  <Badge className={`border-0 ${j.status === "unassigned" ? "bg-amber-100 text-amber-700" : j.status === "completed" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>{j.status === "unassigned" ? "requested" : j.status}</Badge>
                  {j.status === "unassigned" && <button onClick={() => cancel(j.id)} className="text-slate-300 hover:text-red-500" data-testid={`collection-cancel-${j.id}`}><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-400">Orders</h2>
      <div className="mt-3 space-y-3" data-testid="client-orders">
        {orders.map((o) => (
          <div key={o.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`client-order-${o.ref}`}>
            <div className="flex items-center justify-between">
              <div><p className="font-mono text-xs text-slate-400">#{o.ref}</p><p className="font-medium text-slate-900">{money(o.total)} · {o.items.length} item{o.items.length !== 1 ? "s" : ""}</p></div>
              <div className="flex items-center gap-2">
                <Badge className={`border-0 ${STATUS_COLORS[o.status]}`}>{o.status.replace(/_/g, " ")}</Badge>
                <Button size="sm" variant="outline" onClick={() => reorder(o)} className="h-8 rounded-full border-brand/30 text-brand hover:bg-accent" data-testid={`client-reorder-${o.ref}`}><Repeat className="mr-1 h-3.5 w-3.5" /> Reorder</Button>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-400">{o.items.map((i) => i.unit === "kg" ? `${i.qty} kg · ${i.name}` : `${i.qty}× ${i.name}`).join(", ")}</p>
            {o.pickup_date && <p className="mt-1 text-xs text-slate-400">Pickup: {o.pickup_date} {o.pickup_slot}</p>}
            {o.delivery_photo && (
              <a href={fileUrl(o.delivery_photo)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-brand underline" data-testid={`client-order-proof-${o.ref}`}><ImageIcon className="h-3.5 w-3.5" /> View delivery photo</a>
            )}
            {o.signature && (
              <a href={fileUrl(o.signature)} target="_blank" rel="noreferrer" className="ml-3 mt-2 inline-flex items-center gap-1 text-xs text-brand underline" data-testid={`client-order-signature-${o.ref}`}><ImageIcon className="h-3.5 w-3.5" /> View signature</a>
            )}
          </div>
        ))}
        {!orders.length && <p className="text-sm text-slate-400">No orders yet.</p>}
      </div>
    </div>
  );
}

function NewOrder({ ctx, onDone }) {
  const [lines, setLines] = useState([]);
  const [meta, setMeta] = useState({ pickup_date: "", pickup_slot: "", notes: "" });
  const items = ctx?.items || [];
  const addLine = (item) => setLines((ls) => {
    const ex = ls.find((l) => l.item_id === item.id);
    if (ex) return ls.map((l) => l.item_id === item.id ? { ...l, qty: l.qty + 1 } : l);
    return [...ls, { item_id: item.id, name: item.name, price: item.price, qty: 1 }];
  });
  const setQty = (id, qty) => setLines((ls) => ls.map((l) => l.item_id === id ? { ...l, qty: Math.max(1, qty) } : l));
  const total = lines.reduce((s, l) => s + l.price * l.qty, 0);

  const submit = async () => {
    if (!lines.length) return toast.error("Add at least one item");
    try { await api.post("/client/orders", { items: lines, ...meta }); toast.success("Order placed!"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Place an order</h1>
      <p className="mt-1 text-sm text-slate-500">with {ctx?.business?.name}</p>
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
        <Label>Add items</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {items.map((it) => <button key={it.id} onClick={() => addLine(it)} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs hover:border-brand hover:text-brand" data-testid={`client-add-item-${it.id}`}>+ {it.name} · {money(it.price)}/{it.unit}</button>)}
          {!items.length && <p className="text-sm text-slate-400">This laundry hasn't published a price list yet.</p>}
        </div>
        <div className="mt-4 space-y-2">
          {lines.map((l) => (
            <div key={l.item_id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
              <span>{l.name}</span>
              <div className="flex items-center gap-2"><Input type="number" min="1" value={l.qty} onChange={(e) => setQty(l.item_id, Number(e.target.value))} className="h-8 w-16" /><span className="w-16 text-right font-medium">{money(l.price * l.qty)}</span></div>
            </div>
          ))}
        </div>
        {lines.length > 0 && <p className="mt-3 text-right font-semibold">Total: {money(total)}</p>}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Preferred pickup date</Label><Input type="date" value={meta.pickup_date} onChange={(e) => setMeta((m) => ({ ...m, pickup_date: e.target.value }))} data-testid="client-pickup-date" /></div>
          <div className="space-y-1.5"><Label>Preferred slot</Label><Input placeholder="09:00-11:00" value={meta.pickup_slot} onChange={(e) => setMeta((m) => ({ ...m, pickup_slot: e.target.value }))} data-testid="client-pickup-slot" /></div>
        </div>
        <Textarea className="mt-3" placeholder="Notes (optional)" value={meta.notes} onChange={(e) => setMeta((m) => ({ ...m, notes: e.target.value }))} data-testid="client-notes" />
        <Button onClick={submit} className="mt-4 w-full rounded-full bg-brand hover:bg-brand-hover" data-testid="client-place-order-btn">Place order</Button>
      </div>
    </div>
  );
}

function ClientRecurring({ ctx }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [weekday, setWeekday] = useState("0");
  const [slot, setSlot] = useState("");
  const [lines, setLines] = useState([]);
  const items = ctx?.items || [];
  const load = useCallback(() => api.get("/client/recurring").then((r) => { setRows(r.data); setLoaded(true); }), []);
  useEffect(() => { load(); }, [load]);

  const addLine = (it) => setLines((ls) => {
    const ex = ls.find((l) => l.item_id === it.id);
    if (ex) return ls.map((l) => l.item_id === it.id ? { ...l, qty: l.qty + 1 } : l);
    return [...ls, { item_id: it.id, name: it.name, price: it.price, qty: 1 }];
  });
  const setQty = (id, qty) => setLines((ls) => ls.map((l) => l.item_id === id ? { ...l, qty: Math.max(1, qty) } : l));

  const create = async () => {
    if (!lines.length) return toast.error("Add at least one item");
    try {
      await api.post("/client/recurring", { items: lines, weekday: Number(weekday), pickup_slot: slot });
      toast.success("Standing weekly order set up"); setLines([]); setSlot(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Cancel this standing order?")) return; await api.delete(`/client/recurring/${id}`); load(); };

  return (
    <div className="max-w-2xl">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Recurring orders</h1>
      <p className="mt-1 text-sm text-slate-500">Set a standing weekly order and we&rsquo;ll place it automatically with {ctx?.business?.name}.</p>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Repeats every</Label>
            <Select value={weekday} onValueChange={setWeekday}>
              <SelectTrigger data-testid="client-recurring-weekday"><SelectValue /></SelectTrigger>
              <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Preferred slot</Label><Input placeholder="09:00-11:00" value={slot} onChange={(e) => setSlot(e.target.value)} data-testid="client-recurring-slot" /></div>
        </div>
        <div className="mt-3">
          <Label>Items</Label>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {items.map((it) => <button key={it.id} onClick={() => addLine(it)} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs hover:border-brand hover:text-brand" data-testid={`client-recurring-add-${it.id}`}>+ {it.name} · {money(it.price)}</button>)}
          </div>
          <div className="mt-3 space-y-2">
            {lines.map((l) => (
              <div key={l.item_id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                <span>{l.name}</span>
                <div className="flex items-center gap-2"><Input type="number" min="1" value={l.qty} onChange={(e) => setQty(l.item_id, Number(e.target.value))} className="h-8 w-16" /><span className="w-16 text-right font-medium">{money(l.price * l.qty)}</span></div>
              </div>
            ))}
          </div>
        </div>
        <Button onClick={create} className="mt-4 rounded-full bg-brand hover:bg-brand-hover" data-testid="client-recurring-create"><Plus className="mr-1 h-4 w-4" /> Set up standing order</Button>
      </div>

      <div className="mt-5 space-y-3">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4" data-testid={`client-recurring-card-${r.id}`}>
            <div>
              <p className="font-medium text-slate-900">Every {r.weekday_label} · {money(r.total)}/week</p>
              <p className="text-xs text-slate-400">{r.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</p>
              <p className="text-xs text-slate-400">Next: {r.next_run}</p>
            </div>
            <button onClick={() => remove(r.id)} className="text-slate-300 hover:text-red-500" data-testid={`client-recurring-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!rows.length && <p className="text-sm text-slate-400">{loaded ? "No standing orders yet." : "Loading…"}</p>}
      </div>
    </div>
  );
}

function MyInvoices({ invoices, ctx, loaded }) {
  const payEnabled = ctx?.online_payments_enabled;
  const pay = async (inv) => {
    try {
      const { data } = await api.post(`/client/invoices/${inv.id}/pay`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pdf = async (inv) => {
    try { await downloadInvoicePdf(inv.id, inv.number); }
    catch (e) { toast.error("Could not download PDF"); }
  };
  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Invoices</h1>
        <Button variant="outline" className="rounded-full" onClick={() => downloadPdf("/client/statement", "statement.pdf").catch(() => toast.error("Could not download"))} data-testid="client-statement-btn"><Download className="mr-1 h-4 w-4" /> Statement</Button>
      </div>
      <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="client-invoices">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400"><tr><th className="px-4 py-3">Number</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Due</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {invoices.map((inv) => (
              <tr key={inv.id} data-testid={`client-invoice-${inv.number}`}>
                <td className="px-4 py-3 font-mono text-xs">{inv.number}</td>
                <td className="px-4 py-3 font-medium">{money(inv.total)}</td>
                <td className="px-4 py-3 text-slate-500">{inv.due_date || "—"}</td>
                <td className="px-4 py-3"><Badge className={`border-0 ${inv.status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{inv.status}</Badge></td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    {inv.status !== "paid" && payEnabled && <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => pay(inv)} data-testid={`client-pay-${inv.number}`}><CreditCard className="mr-1 h-3 w-3" /> Pay online</Button>}
                    <Button size="sm" variant="outline" onClick={() => pdf(inv)} data-testid={`client-invoice-pdf-${inv.number}`}><Download className="mr-1 h-3 w-3" /> PDF</Button>
                  </div>
                </td>
              </tr>
            ))}
            {!invoices.length && <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">{loaded ? "No invoices yet." : "Loading…"}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
